/**
 * @file Query-key factories for the lab SPA's TanStack queries.
 * @module @labcraft/lab/ui/hooks/query-keys
 *
 * One file owns the `['lab', <sub>, <method>, ...args]` convention so
 * the hook layer and any imperative cache reader
 * (`queryClient.setQueryData` on 401, mutation `onSuccess`
 * invalidations) agree on shape without re-deriving it.
 *
 * Two flavors per key:
 *
 *   - **Const tuples** for argument-free keys (`qkMe`). Captured as
 *     `as const` so TanStack's tuple-typed `queryKey` parameter
 *     narrows correctly.
 *   - **Factory functions** for keys that include args (`qkChapter`).
 *     The factory returns a fresh tuple per call — TanStack's
 *     structural equality on `queryKey` handles identity, so we
 *     don't memoize.
 *
 * Prefixes are intentionally short so mutations can invalidate by
 * prefix (`invalidateQueries({ queryKey: ['lab', 'account'] })`
 * matches every account-scoped query).
 */

////////////////////////////////////////////////////////////////////////////////
// account
////////////////////////////////////////////////////////////////////////////////

/** Identity (current learner) key. Single global query. */
export const qkMe = ['lab', 'account', 'me'] as const

/** Daily VM-time budget key (#941). Single global query (session learner). */
export const qkBudget = ['lab', 'account', 'budget'] as const

////////////////////////////////////////////////////////////////////////////////
// config
////////////////////////////////////////////////////////////////////////////////

/** Runtime-config (analytics) key. Single global query, fetched once. */
export const qkConfig = ['lab', 'config'] as const

////////////////////////////////////////////////////////////////////////////////
// chapter
////////////////////////////////////////////////////////////////////////////////

/**
 * Per-chapter combined-endpoint key. Reserved for a future
 * `useChapter` migration off the bespoke `useChapter()` in
 * `pages/chapter.tsx` — Phase 11b moves identity onto TanStack but
 * keeps the page-local fetcher as-is to bound the diff.
 */
export function qkChapter(courseId: string, partId: string, chapterId: string) {
  return ['lab', 'chapter', courseId, partId, chapterId] as const
}

////////////////////////////////////////////////////////////////////////////////
// courses
////////////////////////////////////////////////////////////////////////////////

/**
 * The session learner's passed-Part set for one course (#1446 Part B).
 * Per-course key so switching courses fetches its own progress.
 */
export function qkPassedParts(courseId: string) {
  return ['lab', 'courses', 'passed-parts', courseId] as const
}
